import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { HeroMedia, useMedia } from "@/components/common/HeroMedia";
import { Intro } from "@/components/common/Intro";
import { Container, Reveal, Img, ParallaxImg, EASE } from "@/components/common/Primitives";
import { SplitWords, Kicker, Headline, Lead, SectionHead, Rows, ClosingBand, RuleDraw, TextLink } from "@/components/common/Editorial";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { PHONE } from "@/components/layout/Header";
import { useLang } from "@/lib/i18n";
import { useContent, minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";
import { imgProps, SIZES } from "@/lib/images";

/* ------------------------------------------------------------------ copy (binding – memory/copy_deck_nynorsk.md §2) */

const HERO = {
  kicker: ["Rosendal · Hardangerfjorden", "Rosendal · the Hardangerfjord"],
  title: ["Fjorden rett utanfor.", "The fjord right outside."],
  /** deliberate two-line setting for phones/tablets (balanced, no orphan) */
  titleLines: [["Fjorden rett", "utanfor."], ["The fjord", "right outside."]],
  sub: [
    "Bu ved Hardangerfjorden, med Baroniet i gåavstand og Rosendalsfjella rundt deg. Her finn du rom med fjord- eller fjellutsikt, Restaurant Baronen og hytter heilt nede ved sjøen.",
    "Stay on the Hardangerfjord, with the Barony within walking distance and the Rosendal mountains all around. Rooms with a fjord or mountain view, Restaurant Baronen and cabins right down by the sea.",
  ],
};

/** Room card copy + marks from the deck (§2 Romkort) */
const ROOM_SHORT = {
  "standard-fjord": { nn: ["Balkong med utemøblar og utsikt over Hardangerfjorden.", ["Fjordutsikt", "Balkong"]], en: ["A balcony with outdoor furniture and a view over the Hardangerfjord.", ["Fjord view", "Balcony"]] },
  "standard-mountain": { nn: ["Rosendalsfjella utanfor vindauget og frukost inkludert.", ["Fjellutsikt"]], en: ["The Rosendal mountains outside the window and breakfast included.", ["Mountain view"]] },
  juniorsuite: { nn: ["Fjordutsikt, balkong og ei eiga sitjegruppe.", ["Fjordutsikt", "Sitjegruppe"]], en: ["A fjord view, a balcony and your own seating area.", ["Fjord view", "Seating area"]] },
  brudesuite: { nn: ["Romsleg suite med sitjegruppe og stor balkong. Utsikta varierer mellom dei to suitane.", ["Stor balkong", "Sitjegruppe"]], en: ["A spacious suite with a seating area and a large balcony. The view differs between the two suites.", ["Large balcony", "Seating area"]] },
  superiorsuite: { nn: ["Fjellutsikt, sofa og eige matbord.", ["Fjellutsikt", "Meir plass"]], en: ["A mountain view, a sofa and your own dining table.", ["Mountain view", "More space"]] },
  sidefloy: { nn: ["Eigen inngang på bakkeplan og parkering rett utanfor. Kjæledyr etter avtale.", ["Eigen inngang", "Kjæledyr etter avtale"]], en: ["A private ground-level entrance and parking right outside. Pets by arrangement.", ["Private entrance", "Pets by arrangement"]] },
};

const DINING_ROWS = [
  { nn: ["Lunsj", "À la carte kvar dag kl. 13.00–17.00."], en: ["Lunch", "À la carte every day 13:00–17:00."] },
  { nn: ["Middag", "Tre rettar med servering kl. 19.00, 20.00 eller 21.00. Fem rettar på førespurnad."], en: ["Dinner", "Three courses served at 19:00, 20:00 or 21:00. Five courses on request."] },
  { nn: ["Vin og sider", "Smaking med sommelier Beate Hannisdal, laurdagar kl. 16.00 eller etter avtale."], en: ["Wine and cider", "Tasting with sommelier Beate Hannisdal, Saturdays at 16:00 or by arrangement."] },
];

/** Deck order: Baroniet · Badstove · Fjellturar · Bondhusvatnet. Laid out as a staggered editorial grid. */
const ROSENDAL = [
  { src: "/images/baroniet-roses.jpg", position: "50% 55%", nn: ["Baroniet Rosendal", "Besøk slottet og den historiske hagen, ein spasertur frå hotellet."], en: ["The Barony Rosendal", "Visit the manor and its historic garden, a short walk from the hotel."] },
  { src: "/images/pier.jpg", position: "50% 55%", nn: ["Badstove ved stranda", "Utsikt mot fjorden og Snilstveitøy, med sjøen rett utanfor."], en: ["Sauna by the beach", "Looking out over the fjord and Snilstveitøy, with the sea right outside."] },
  { src: "/images/peaks-hiker.jpg", position: "40% 50%", nn: ["Fjellturar", "Frå Hattebergsfossen til Malmangernuten og Melderskin. Vel tur etter tid, form og vêr."], en: ["Mountain hikes", "From Hattebergsfossen to Malmangernuten and Melderskin. Pick a hike to suit your time, shape and the weather."] },
  { src: "/images/skalafjell.jpg", position: "50% 50%", nn: ["Bondhusvatnet", "Set av ein dag til ein tur i Bondhusdalen."], en: ["Bondhusvatnet", "Set aside a day for a walk in the Bondhus valley."] },
];
/* grid placement + aspect for the four Rosendal pictures (desktop: 12 columns, deliberately uneven) */
const ROSENDAL_LAYOUT = [
  { cell: "col-span-12 lg:col-span-7", aspect: "aspect-[4/3]", range: 4 },
  { cell: "col-span-9 col-start-4 lg:col-span-4 lg:col-start-9 lg:mt-32", aspect: "aspect-[4/5]", range: 5 },
  { cell: "col-span-10 lg:col-span-4 lg:col-start-2 lg:mt-10", aspect: "aspect-[4/5]", range: 5 },
  { cell: "col-span-12 lg:col-span-6 lg:col-start-7 lg:mt-36", aspect: "aspect-[3/2]", range: 4 },
];

const HOME_EVENTS = ["wedding", "conference", "experience"];
const EVENT_FALLBACK = {
  wedding: { img: "/images/wedding.jpg", position: "50% 40%", nn: ["Bryllaup og selskap", "Lokale til både små og store feiringar, med mat frå Baronen og rom til gjester som vil overnatte.", "Planlegg selskapet"], en: ["Weddings and celebrations", "Venues for small and large celebrations, with food from Baronen and rooms for guests who want to stay over.", "Plan your celebration"] },
  conference: { img: "/images/meeting-room.jpg", position: "50% 50%", nn: ["Møte og konferanse", "Møtelokale i ulike storleikar, med projektor, lerret og trådlaust nett. Me lagar eit forslag ut frå talet på deltakarar og programmet dykkar.", "Spør om møte"], en: ["Meetings and conferences", "Meeting rooms in several sizes, with projector, screen and wireless internet. We draft a proposal based on your numbers and programme.", "Ask about a meeting"] },
  experience: { img: "/images/reception-bubbles.jpg", position: "50% 50%", nn: ["Smaking og utflukter", "La ei smaking eller ein tur i Rosendal bli ein del av samlinga. Me hjelper dykk å finne eit opplegg som passar.", "Sjå moglegheitene"], en: ["Tastings and excursions", "Make a tasting or a trip around Rosendal part of your gathering. We help you find a programme that fits.", "See the options"] },
};

const CABIN_FACTS = {
  nn: [["Gjester", "Inntil åtte"], ["Rom", "Stove og kjøkenkrok"], ["Ute", "Eigen terrasse mot fjorden"], ["Opphald", "Minst to netter"]],
  en: [["Guests", "Up to eight"], ["Inside", "Living room and kitchenette"], ["Outside", "Private terrace facing the fjord"], ["Stay", "Minimum two nights"]],
};

/** Rise-in for hero copy. `start=false` holds everything hidden until the intro curtain has lifted. */
const useUp = (reduce, start = true) => (delay = 0) => ({
  initial: reduce ? false : { opacity: 0, y: 14 },
  animate: start ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 },
  transition: { duration: 0.9, delay, ease: EASE },
});

/** Text-only trust line under the search – facts left, terms right (deck §1). `light` = on the photo. */
const TrustRow = ({ light = false, className = "", testid = "home-trust-row" }) => {
  const { tx } = useLang();
  const facts = [tx("Frukost inkludert i hotellromma", "Breakfast included in hotel rooms"), tx("Gratis parkering", "Free parking"), tx("Bestill direkte", "Book direct")];
  return (
    <div
      className={`flex flex-col text-[0.8125rem] ${light ? "gap-2 text-white/70" : "gap-2.5 border-b border-border pb-6 text-muted-foreground lg:flex-row lg:items-center lg:justify-between lg:gap-10"} ${className}`}
      data-testid={testid}
    >
      <p className="flex shrink-0 flex-wrap items-center gap-y-1.5">
        {facts.map((t, i) => (
          <span key={t} className={`label-caps whitespace-nowrap ${light ? "hero-shadow text-white/85" : "text-foreground/80"}`}>
            {t}
            {i < facts.length - 1 && <span className={`mx-3 inline-block h-[3px] w-[3px] rounded-full align-middle ${light ? "bg-white/40" : "bg-foreground/30"}`} aria-hidden />}
          </span>
        ))}
      </p>
      <p className={`leading-relaxed ${light ? "hero-shadow max-w-3xl" : "max-w-xl lg:text-right"}`}>
        {tx("For rom bestilte på nettsida: gratis endring og avbestilling fram til kl. 18.00 dagen før ankomst.", "For rooms booked on the website: free changes and cancellation until 18:00 the day before arrival.")}{" "}
        <Link
          to="/praktisk#vilkar"
          className={`underline underline-offset-4 transition-colors ${light ? "decoration-white/40 hover:text-white hover:decoration-white" : "decoration-border hover:text-foreground hover:decoration-foreground"}`}
          data-testid="home-terms-link"
        >
          {tx("Sjå bestillingsvilkåra", "See the booking terms")}
        </Link>
      </p>
    </div>
  );
};

/* ------------------------------------------------------------------ hero */

const HERO_IMG = "/images/hero-fjord-aerial.jpg";
const HERO_VIDEO = {
  mp41080: "/videos/hero-1080.mp4",
  mp4720: "/videos/hero-720.mp4",
  webm720: "/videos/hero-720.webm", // fallback for browsers without H.264
};

/** Save-Data users get the still; everyone else (incl. phones) gets the film unless they prefer reduced motion. */
const useWantsFilm = (reduce) => {
  const saveData = typeof navigator !== "undefined" && !!navigator.connection?.saveData;
  return !reduce && !saveData;
};

/**
 * First screen on phones and tablets: the film is shown in its own landscape format (3:2 on phones,
 * 16:9 on tablets) edge to edge, with the title above it and the intro, the search card and the
 * facts below – nothing is laid over the picture, so the whole frame of the film is visible.
 * The header is solid here (no `data-photo-hero`).
 */
const MobileHero = ({ start = true }) => {
  const { tx } = useLang();
  const reduce = useReducedMotion();
  const up = useUp(reduce, start);
  const useVideo = useWantsFilm(reduce);
  return (
    <section className="relative pt-6 sm:pt-10" data-testid="home-hero" data-hero-layout="landscape">
      <Container>
        <motion.p {...up(0.1)} className="label-caps text-muted-foreground" data-testid="home-hero-kicker">{tx(...HERO.kicker)}</motion.p>
        <h1 className="mt-3 text-[clamp(2.75rem,12.5vw,3.75rem)] font-medium leading-[0.94] tracking-[-0.045em] text-foreground sm:text-[clamp(3.25rem,8vw,5rem)]" data-testid="home-hero-title">
          <SplitWords lines={tx(...HERO.titleLines)} play={start} delay={0.2} stagger={0.08} duration={1.1} />
        </h1>
      </Container>
      <motion.div {...up(0.35)} className="relative mt-6 aspect-[3/2] w-full overflow-hidden bg-fjord sm:mt-9 sm:aspect-[16/9]" data-testid="home-hero-photo" data-video={useVideo ? "on" : "off"}>
        <HeroMedia
          image={HERO_IMG}
          alt={tx("Rosendal Fjordhotel, brygga og Hardangerfjorden sett frå lufta", "Rosendal Fjordhotel, the pier and the Hardangerfjord from the air")}
          position="50% 42%"
          video={HERO_VIDEO}
          enabled={useVideo}
          label={tx("Rosendal Fjordhotel – film frå hotellet, brygga og hagen mot fjorden", "Rosendal Fjordhotel – film of the hotel, the pier and the garden by the fjord")}
          testid="home-hero-media"
        />
      </motion.div>
      <Container className="mt-7 sm:mt-9">
        <motion.p {...up(0.5)} className="max-w-[40rem] text-[1.125rem] leading-[1.55] text-foreground/85 sm:text-[1.1875rem]" data-testid="home-hero-sub">{tx(...HERO.sub)}</motion.p>
        <motion.div {...up(0.6)} className="mt-8 sm:mt-10" data-sticky-anchor data-testid="home-booking-strip">
          <BookingBar variant="hero" size="xl" />
        </motion.div>
        <motion.div {...up(0.7)} className="mt-6">
          <TrustRow />
        </motion.div>
      </Container>
    </section>
  );
};

/**
 * First screen on desktop. The drone still of the hotel, pier and fjord – shown instantly; the hotel
 * film fades in over it once it plays. Copy sits in the lower half, and the search bar is part of
 * the same screen: full width, white on the dark scrim, with the breakfast/parking/direct-booking
 * facts and the terms right under it. Nothing else.
 */
const Hero = ({ start = true }) => {
  const { tx } = useLang();
  const reduce = useReducedMotion();
  const up = useUp(reduce, start);
  const isMobile = useIsMobile();
  const useVideo = useWantsFilm(reduce);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "10%"]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);

  if (isMobile) return <MobileHero start={start} />;

  return (
    <section className="relative" data-testid="home-hero" data-photo-hero data-hero-layout="fullscreen">
      <div ref={ref} className="relative h-[100svh] max-h-[1100px] min-h-[680px] overflow-hidden bg-fjord" data-testid="home-hero-photo" data-video={useVideo ? "on" : "off"}>
        <motion.div style={{ y: mediaY }} className="absolute inset-x-0 -top-[5%] h-[110%] will-change-transform">
          <HeroMedia
            image={HERO_IMG}
            alt={tx("Rosendal Fjordhotel, brygga og Hardangerfjorden sett frå lufta", "Rosendal Fjordhotel, the pier and the Hardangerfjord from the air")}
            position="50% 30%"
            video={HERO_VIDEO}
            enabled={useVideo}
            label={tx("Rosendal Fjordhotel – film frå hotellet, brygga og hagen mot fjorden", "Rosendal Fjordhotel – film of the hotel, the pier and the garden by the fjord")}
            testid="home-hero-media"
          />
        </motion.div>
        <div className="scrim-nav pointer-events-none absolute inset-x-0 top-0 h-40" aria-hidden />
        <div className="scrim-video pointer-events-none absolute inset-x-0 bottom-0 h-[78%]" aria-hidden />
        <div className="scrim-hero-left pointer-events-none absolute inset-0" aria-hidden />

        <Container className="relative flex h-full flex-col">
          <motion.div style={{ opacity: copyOpacity }} className="flex flex-1 flex-col justify-end pb-12 xl:pb-14">
            <motion.p {...up(0.1)} className="label-caps hero-shadow text-white/80" data-testid="home-hero-kicker">{tx(...HERO.kicker)}</motion.p>
            <h1 className="hero-shadow mt-5 whitespace-nowrap text-[clamp(3.5rem,5.8vw,7rem)] font-medium leading-[0.96] tracking-[-0.04em] text-white" data-testid="home-hero-title">
              <SplitWords text={tx(...HERO.title)} play={start} delay={0.2} stagger={0.08} duration={1.1} />
            </h1>
            <motion.p {...up(0.55)} className="hero-shadow mt-8 max-w-[44rem] text-[1.25rem] leading-[1.6] text-white/95" data-testid="home-hero-sub">{tx(...HERO.sub)}</motion.p>
          </motion.div>

          {/* Search – inside the first screen */}
          <motion.div {...up(0.75)} className="pb-8 xl:pb-10" data-sticky-anchor data-testid="home-booking-strip">
            <BookingBar variant="hero" size="xl" />
            <TrustRow light className="mt-5" />
          </motion.div>
        </Container>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ rooms – index list with a sticky photo stage */

/**
 * Phones and tablets: the rooms as a swipeable row of large portrait cards (snap scrolling), with a
 * thin position line underneath. The next card peeks in from the right so the gesture is obvious.
 */
const RoomRailMobile = ({ rooms, byBuilding, shortFor }) => {
  const { lang, tx, tr } = useLang();
  const railRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const onScroll = () => {
    const el = railRef.current;
    const first = el?.firstElementChild;
    if (!el || !first) return;
    const step = first.getBoundingClientRect().width + parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || "16");
    setIdx(Math.max(0, Math.min(rooms.length - 1, Math.round(el.scrollLeft / step))));
  };
  return (
    <div data-testid="home-rooms-index">
      <div
        ref={railRef}
        onScroll={onScroll}
        className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-5 px-5 pb-1 sm:-mx-8 sm:gap-6 sm:scroll-px-8 sm:px-8"
        data-testid="home-rooms-rail"
      >
        {rooms.map((rt, i) => {
          const id = `home-room-card-${rt.id}`;
          const price = minPrice(rt);
          const [body] = shortFor(rt);
          return (
            <Link
              key={rt.id}
              to={`/rom/${rt.slug}`}
              className="group w-[78vw] max-w-[420px] shrink-0 snap-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 sm:w-[46vw]"
              data-testid={id}
              aria-label={tr(rt.name)}
            >
              <Img src={rt.images?.[0]} alt="" className="img-zoom aspect-[4/5]" sizes={SIZES.half} reveal={false} eager={i < 2} />
              <div className="mt-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <span className="label-caps block text-muted-foreground">{byBuilding[rt.building] ? tr(byBuilding[rt.building].name) : tx("Rom", "Room")}</span>
                  <h3 className="mt-1.5 text-[1.375rem] font-medium leading-[1.1] tracking-[-0.02em] text-foreground" data-testid={`${id}-title`}>{tr(rt.name)}</h3>
                </div>
                {price && (
                  <p className="shrink-0 text-right">
                    <span className="label-caps block text-muted-foreground">{tx("Frå", "From")}</span>
                    <span className="mt-1 block text-[1.0625rem] font-medium leading-none text-foreground tnum" data-testid={`${id}-price`}>{nok(price, lang)}</span>
                  </p>
                )}
              </div>
              <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-[0.9375rem] font-medium text-foreground" data-testid={`${id}-link`}>
                <span className="link-grow">{tx("Sjå rommet", "See the room")}</span>
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </span>
            </Link>
          );
        })}
      </div>
      <div className="mt-6 flex items-center gap-5" aria-hidden>
        <span className="index-num tnum text-muted-foreground" data-testid="home-rooms-rail-index">{String(idx + 1).padStart(2, "0")} / {String(rooms.length).padStart(2, "0")}</span>
        <span className="relative h-px flex-1 bg-border">
          <span className="absolute inset-y-0 left-0 bg-foreground transition-transform duration-300 ease-premium" style={{ width: `${100 / rooms.length}%`, transform: `translateX(${idx * 100}%)` }} />
        </span>
        <span className="label-caps text-muted-foreground">{tx("Sveip", "Swipe")}</span>
      </div>
    </div>
  );
};

/**
 * The rooms as an index, not a carousel. Desktop: one large photo on the left that crossfades as you
 * move over the list on the right; the active row opens to show its line and "Sjå rommet".
 * Phones/tablets: `RoomRailMobile`.
 */
const RoomIndex = ({ rooms, byBuilding, loading }) => {
  const { lang, tx, tr } = useLang();
  const isMobile = useIsMobile();
  const [active, setActive] = useState(0);
  const key = rooms.map((r) => r.id).join("|");
  useEffect(() => setActive(0), [key]);
  const safeActive = Math.min(active, Math.max(rooms.length - 1, 0));
  const current = rooms[safeActive];

  if (loading) {
    return (
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-14" data-testid="home-rooms-loading">
        <Skeleton className="hidden aspect-[4/3] rounded-photo lg:col-span-7 lg:block" />
        <div className="space-y-px lg:col-span-5">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-[88px] rounded-none" />)}
        </div>
      </div>
    );
  }
  if (!rooms.length) {
    return <p className="border-y border-border py-10 text-[0.9375rem] text-muted-foreground" data-testid="home-rooms-empty">{tx("Ingen rom i denne kategorien enno.", "No rooms in this category yet.")}</p>;
  }

  const shortFor = (rt) => (ROOM_SHORT[rt.id] ? (lang === "en" ? ROOM_SHORT[rt.id].en : ROOM_SHORT[rt.id].nn) : [tr(rt.tagline), []]);

  if (isMobile) return <RoomRailMobile rooms={rooms} byBuilding={byBuilding} shortFor={shortFor} />;

  return (
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-14" data-testid="home-rooms-index">
      {/* stage – the column stretches to the list's height so the photo can stick while you move down it */}
      <div className="hidden lg:col-span-7 lg:block">
        <div className="relative aspect-[4/3] overflow-hidden rounded-photo bg-muted lg:sticky lg:top-24" data-testid="home-rooms-stage" data-active-room={current?.id}>
          {rooms.map((rt, i) => (
            <img
              key={rt.id}
              {...imgProps(rt.images?.[0], SIZES.half)}
              alt={i === safeActive ? tr(rt.name) : ""}
              aria-hidden={i !== safeActive}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              className={`absolute inset-0 h-full w-full object-cover transition-[opacity,transform] [transition-duration:900ms] ease-premium ${i === safeActive ? "scale-100 opacity-100" : "scale-[1.04] opacity-0"}`}
            />
          ))}
          <div className="scrim-bottom pointer-events-none absolute inset-x-0 bottom-0 h-[40%]" aria-hidden />
          {current && (
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-7 xl:p-8">
              <p className="hero-shadow text-white">
                <span className="label-caps block text-white/70">{byBuilding[current.building] ? tr(byBuilding[current.building].name) : tx("Rom", "Room")}</span>
                <span className="mt-1.5 block text-[1.375rem] font-medium leading-tight tracking-[-0.02em]" data-testid="home-rooms-stage-title">{tr(current.name)}</span>
              </p>
              <Button asChild variant="inverse" className="shrink-0" data-testid="home-rooms-stage-cta">
                <Link to={`/rom/${current.slug}`}>{tx("Sjå rommet", "See the room")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* index */}
      <ol className="border-t border-border lg:col-span-5" data-testid="home-rooms-grid">
        {rooms.map((rt, i) => {
          const id = `home-room-card-${rt.id}`;
          const price = minPrice(rt);
          const [body, marks] = shortFor(rt);
          const isActive = i === safeActive;
          const label = [byBuilding[rt.building] ? tr(byBuilding[rt.building].name) : null, ...marks].filter(Boolean).join(" · ");
          return (
            <li key={rt.id}>
              <Link
                to={`/rom/${rt.slug}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                data-active={isActive ? "true" : "false"}
                data-testid={id}
                aria-label={tr(rt.name)}
                className="group grid grid-cols-[4.5rem_1fr] items-start gap-5 border-b border-border py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 lg:grid-cols-[2.25rem_1fr_auto] lg:gap-6 lg:py-6"
              >
                <Img src={rt.images?.[0]} alt="" className="aspect-square lg:hidden" radius="rounded-[4px]" sizes={SIZES.thumb} reveal={false} />
                <span className={`index-num hidden pt-3 tnum transition-colors duration-200 lg:block ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{String(i + 1).padStart(2, "0")}</span>
                <span className="min-w-0">
                  <span className="flex items-baseline justify-between gap-4">
                    <span className={`block text-[1.1875rem] font-medium leading-[1.15] tracking-[-0.02em] transition-colors duration-200 lg:text-[1.625rem] ${isActive ? "text-foreground" : "text-foreground lg:text-foreground/60 lg:group-hover:text-foreground"}`} data-testid={`${id}-title`}>{tr(rt.name)}</span>
                    {price && (
                      <span className="shrink-0 text-right text-[0.9375rem] text-muted-foreground lg:hidden">
                        <span className="tnum font-medium text-foreground">{nok(price, lang)}</span>
                      </span>
                    )}
                  </span>
                  <span className="label-caps mt-2 block text-muted-foreground">{label}</span>
                  <span className="grid transition-[grid-template-rows] duration-500 ease-premium [grid-template-rows:0fr] lg:group-data-[active=true]:[grid-template-rows:1fr]" aria-hidden={!isActive}>
                    <span className="block min-h-0 overflow-hidden">
                      <span className="mt-3 block max-w-[46ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</span>
                      <span className="mt-4 inline-flex items-center gap-2 text-[0.9375rem] font-medium text-foreground" data-testid={`${id}-link`}>
                        <span className="link-grow">{tx("Sjå rommet", "See the room")}</span>
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.5} />
                      </span>
                      <span className="block h-1" aria-hidden />
                    </span>
                  </span>
                </span>
                {price && (
                  <span className="hidden pt-2 text-right text-sm text-muted-foreground lg:block">
                    <span className="block">{tx("Frå", "From")}</span>
                    <span className={`mt-0.5 block text-[1.0625rem] font-medium leading-none tnum transition-colors duration-200 ${isActive ? "text-foreground" : "text-foreground/60"}`} data-testid={`${id}-price`}>{nok(price, lang)}</span>
                    <span className="mt-1 block text-xs">{tx("per natt", "per night")}</span>
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/* ------------------------------------------------------------------ featured room – pinned, photo grows to full bleed */

const FEATURED = {
  img: "/images/room-fjord-window.jpg",
  alt: ["Standardrom med fjordutsikt – vindauge mot fjorden og fjella", "Standard room with fjord view – window facing the fjord and mountains"],
};

const FeaturedCopy = ({ fjordRoom, seaFrom, style, className = "" }) => {
  const { lang, tx } = useLang();
  return (
    <motion.div style={style} className={`flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between ${className}`}>
      <div className="max-w-xl">
        <Kicker light className="hero-shadow">{tx("Standardrom med fjordutsikt", "Standard room with fjord view")}</Kicker>
        <Headline light size="lg" split={false} className="hero-shadow mt-5" testid="home-chair-band-title">{tx("Med eigen balkong mot fjorden", "With your own balcony facing the fjord")}</Headline>
        <Lead light className="hero-shadow">{tx("Opne døra til balkongen og set deg ut ei stund. Standardromma med fjordutsikt har utemøblar og utsikt over Hardangerfjorden.", "Open the balcony door and sit outside for a while. The fjord-view standard rooms have outdoor furniture and a view over the Hardangerfjord.")}</Lead>
      </div>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-5">
        {seaFrom && (
          <p className="hero-shadow text-white">
            <span className="label-caps block text-white/65">{tx("Frå", "From")}</span>
            <span className="mt-1 block text-[1.75rem] font-medium leading-none tracking-[-0.01em] tnum" data-testid="home-seaview-price">{nok(seaFrom, lang)}</span>
            <span className="mt-1 block text-sm text-white/65">{tx("per natt · frukost inkludert", "per night · breakfast included")}</span>
          </p>
        )}
        <Button asChild size="lg" variant="inverse" data-testid="home-chair-band-cta">
          <Link to={fjordRoom ? `/rom/${fjordRoom.slug}` : "/rom"}>{tx("Sjå rom med fjordutsikt", "See fjord-view rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link>
        </Button>
      </div>
    </motion.div>
  );
};

/**
 * Desktop: the section is 2.2 screens tall and its content is pinned. While you scroll, the room
 * photo grows from a framed inset (60 %) to the full viewport and the copy rises in – a single,
 * cinematic beat. Phones and reduced-motion get the plain full-bleed band.
 */
const FeaturedRoom = ({ fjordRoom, seaFrom }) => {
  const { tx } = useLang();
  const reduce = useReducedMotion();
  const desktop = useMedia();
  const pin = desktop && !reduce;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const frame = useTransform(scrollYProgress, [0, 0.55], [0.6, 1]);
  const radius = useTransform(scrollYProgress, [0, 0.55], [10, 0]);
  const zoom = useTransform(scrollYProgress, [0, 1], [1.18, 1.02]);
  const copyO = useTransform(scrollYProgress, [0.42, 0.68], [0, 1]);
  const copyY = useTransform(scrollYProgress, [0.42, 0.68], [28, 0]);
  const alt = tx(...FEATURED.alt);

  if (!pin) {
    return (
      <section className="pt-24 lg:pt-36" data-testid="home-chair-band">
        <div className="relative h-[84svh] max-h-[900px] min-h-[520px] overflow-hidden bg-fjord">
          <ParallaxImg src={FEATURED.img} alt={alt} className="absolute inset-0 h-full w-full" radius="rounded-none" position="50% 55%" range={6} sizes={SIZES.full} />
          <div className="scrim-hero-strong pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" aria-hidden />
          <Container className="relative flex h-full flex-col justify-end pb-10 sm:pb-14 lg:pb-16">
            <Reveal><FeaturedCopy fjordRoom={fjordRoom} seaFrom={seaFrom} /></Reveal>
          </Container>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="relative mt-28 h-[220vh] lg:mt-40" data-testid="home-chair-band" data-pinned>
      <div className="sticky top-0 h-screen overflow-hidden">
        <motion.div style={{ scale: frame, borderRadius: radius }} className="absolute inset-0 overflow-hidden bg-fjord will-change-transform">
          <motion.img
            {...imgProps(FEATURED.img, SIZES.full)}
            alt={alt}
            loading="lazy"
            decoding="async"
            style={{ scale: zoom, objectPosition: "50% 55%" }}
            className="h-full w-full object-cover will-change-transform"
            data-testid="home-chair-band-image"
          />
          <div className="scrim-hero-strong pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" aria-hidden />
        </motion.div>
        <Container className="relative flex h-full flex-col justify-end pb-14 lg:pb-16">
          <FeaturedCopy fjordRoom={fjordRoom} seaFrom={seaFrom} style={{ opacity: copyO, y: copyY }} />
        </Container>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ Rosendal – pinned horizontal rail (desktop) / staggered grid (phones) */

const RosendalHead = ({ className = "" }) => {
  const { tx } = useLang();
  return (
    <div className={className}>
      <Kicker className="mb-5">{tx("Rosendal", "Rosendal")}</Kicker>
      <Headline size="lg" testid="home-experience-title">{tx("Bruk dagen i Rosendal", "Spend the day in Rosendal")}</Headline>
      <Lead>{tx("Gå til Baroniet, finn ein tur som passar deg, eller set av tid til badstova ved stranda. Me hjelper deg gjerne med råd før du legg ut.", "Walk to the Barony, find a hike that suits you, or set aside time for the sauna by the beach. We are happy to help with advice before you set off.")}</Lead>
      <TextLink to="/praktisk" className="mt-8" testid="home-experience-title-action">{tx("Praktisk informasjon", "Practical information")}</TextLink>
    </div>
  );
};

const RosendalCaption = ({ i, title, body }) => (
  <figcaption className="mt-5 flex items-baseline gap-4">
    <span className="index-num text-muted-foreground tnum">{String(i + 1).padStart(2, "0")}</span>
    <span>
      <span className="block text-[1.25rem] font-medium leading-snug tracking-[-0.01em] text-foreground">{title}</span>
      <span className="mt-1.5 block max-w-[40ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</span>
    </span>
  </figcaption>
);

/** Rail photo sizes – a fixed height with varying aspect ratios so the row reads like a contact sheet */
const RAIL_ASPECT = ["4 / 3", "3 / 4", "4 / 3", "3 / 2"];

/**
 * Desktop: the section pins and the row of photos slides sideways exactly as far as you scroll
 * down (1 px = 1 px), so a trackpad or wheel reads naturally and nothing is hijacked.
 * Phones and reduced-motion get the staggered editorial grid.
 */
const RosendalRail = () => {
  const { lang } = useLang();
  const reduce = useReducedMotion();
  const desktop = useMedia();
  const pin = desktop && !reduce;
  const ref = useRef(null);
  const trackRef = useRef(null);
  const [dist, setDist] = useState(0);

  useEffect(() => {
    if (!pin) return undefined;
    const measure = () => {
      const t = trackRef.current;
      if (!t) return;
      setDist(Math.max(0, Math.ceil(t.scrollWidth - window.innerWidth)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  }, [pin, lang]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], [0, -dist]);

  if (!pin) {
    return (
      <section className="pt-28 lg:pt-40" data-testid="home-experience">
        <Container>
          <Reveal><RosendalHead className="max-w-3xl" /></Reveal>
          <div className="mt-14 grid grid-cols-12 gap-x-5 gap-y-14 lg:mt-20 lg:gap-x-10 lg:gap-y-0" data-testid="home-experience-gallery">
            {ROSENDAL.map((it, i) => {
              const [title, body] = lang === "en" ? it.en : it.nn;
              const L = ROSENDAL_LAYOUT[i];
              return (
                <Reveal key={it.src} as="figure" delay={(i % 2) * 0.06} className={L.cell} data-testid={`home-experience-gallery-item-${i}`}>
                  <ParallaxImg src={it.src} alt={title} className={L.aspect} position={it.position} range={L.range} sizes={i === 0 ? SIZES.half : SIZES.third} />
                  <RosendalCaption i={i} title={title} body={body} />
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section ref={ref} className="relative mt-28 lg:mt-40" style={{ height: `calc(100vh + ${dist}px)` }} data-testid="home-experience" data-pinned>
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <motion.div
          ref={trackRef}
          style={{ x }}
          className="flex items-center gap-12 pl-[max(2.5rem,calc((100vw-1280px)/2+2.5rem))] pr-[max(2.5rem,calc((100vw-1280px)/2+2.5rem))] will-change-transform 2xl:gap-16 2xl:pl-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))] 2xl:pr-[max(2.5rem,calc((100vw-1400px)/2+2.5rem))]"
          data-testid="home-experience-gallery"
        >
          <RosendalHead className="w-[clamp(20rem,30vw,34rem)] shrink-0 pr-6" />
          {ROSENDAL.map((it, i) => {
            const [title, body] = lang === "en" ? it.en : it.nn;
            return (
              <figure key={it.src} className="shrink-0" data-testid={`home-experience-gallery-item-${i}`}>
                <div className="h-[56vh] max-h-[640px] min-h-[380px] overflow-hidden rounded-photo bg-muted" style={{ aspectRatio: RAIL_ASPECT[i] }}>
                  <img {...imgProps(it.src, SIZES.half)} alt={title} loading="lazy" decoding="async" style={{ objectPosition: it.position }} className="h-full w-full object-cover" />
                </div>
                <RosendalCaption i={i} title={title} body={body} />
              </figure>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ page */

export default function Home() {
  const { lang, tx, tr } = useLang();
  const { data, isLoading } = useContent();
  const { ask } = useConcierge();
  usePageMeta(
    tx("Rosendal Fjordhotel – Fjorden rett utanfor", "Rosendal Fjordhotel – The fjord right outside"),
    tx("Bu ved Hardangerfjorden, med Baroniet i gåavstand og Rosendalsfjella rundt deg. Rom med fjord- eller fjellutsikt, Restaurant Baronen og hytter heilt nede ved sjøen.", "Stay on the Hardangerfjord, with the Barony within walking distance and the Rosendal mountains all around. Rooms with a fjord or mountain view, Restaurant Baronen and cabins by the sea."),
  );
  const buildings = data?.buildings || [];
  const roomTypes = data?.room_types || [];
  const byBuilding = Object.fromEntries(buildings.map((b) => [b.id, b]));
  const eventTypes = Object.fromEntries((data?.event_types || []).map((e) => [e.id, e]));
  const fjordRoom = roomTypes.find((r) => r.id === "standard-fjord") || roomTypes.find((r) => (r.badges || []).includes("sea_view"));
  const seaFrom = fjordRoom ? minPrice(fjordRoom) : null;

  const questions = lang === "en"
    ? ["Which rooms have a fjord view?", "Can I bring my dog?", "Do you have cabins for larger groups?", "How do I get to the hotel?"]
    : ["Kva rom har fjordutsikt?", "Kan eg ta med hund?", "Har de hytter for fleire?", "Korleis kjem eg til hotellet?"];

  const [filter, setFilter] = useState("all");
  const shownRooms = filter === "all" ? roomTypes : roomTypes.filter((r) => r.building === filter);
  const filterOptions = [{ id: "all", name: { nn: "Alle rom", en: "All rooms" } }, ...buildings];

  // The hero waits for the intro curtain (shown once per session) before it starts its reveal.
  const [introDone, setIntroDone] = useState(false);
  const onIntroDone = useCallback(() => setIntroDone(true), []);

  return (
    <div data-testid="home-page">
      <Intro onDone={onIntroDone} />
      <Hero start={introDone} />

      {/* Rooms – deck §2: filter + "Sjå rommet" */}
      <section className="pt-28 lg:pt-40" data-testid="home-rooms">
        <Container>
          <Reveal>
            <SectionHead
              size="lg"
              kicker={tx("Rom og hytter", "Rooms and cabins")}
              title={tx("Finn rommet ditt", "Find your room")}
              lead={tx("Vil du ha balkong mot fjorden, meir plass i ein suite eller eigen inngang i sidefløya? Sjå kva som skil romma, og finn det som passar opphaldet ditt.", "A balcony facing the fjord, more space in a suite or your own entrance in the side wing? See what sets the rooms apart and find the one that suits your stay.")}
              action={tx("Sjå alle rom og hytter", "See all rooms and cabins")}
              to="/rom"
              testid="home-rooms-title"
            />
          </Reveal>
          <Reveal delay={0.05} className="mt-12 lg:mt-16">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="no-scrollbar -mx-5 flex h-auto w-[calc(100%+2.5rem)] items-end justify-start gap-7 overflow-x-auto rounded-none border-b border-border bg-transparent p-0 px-5 text-muted-foreground sm:mx-0 sm:w-full sm:gap-9 sm:px-0" aria-label={tx("Vel romkategori", "Choose a room category")} data-testid="home-rooms-filter">
                {filterOptions.map((b) => (
                  <TabsTrigger key={b.id} value={b.id} data-testid={`home-rooms-filter-${b.id}`} className="-mb-px shrink-0 rounded-none border-b border-transparent bg-transparent px-0 pb-4 pt-1 text-[0.9375rem] font-medium tracking-[0.01em] text-foreground/55 transition-colors hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none">
                    {tr(b.name)}
                    <span className="index-num ml-2.5 text-muted-foreground tnum">{String(b.id === "all" ? roomTypes.length : roomTypes.filter((r) => r.building === b.id).length).padStart(2, "0")}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </Reveal>
          <Reveal delay={0.08} className="mt-10 lg:mt-12">
            <RoomIndex rooms={shownRooms} byBuilding={byBuilding} loading={isLoading && !roomTypes.length} />
          </Reveal>
        </Container>
      </section>

      {/* Featured: fjord-view room – pinned; the photo grows from a framed inset to full bleed as you scroll */}
      <FeaturedRoom fjordRoom={fjordRoom} seaFrom={seaFrom} />

      {/* Cabins */}
      <section className="pt-28 lg:pt-40" data-testid="home-cabins">
        <Container className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="order-2 lg:order-1 lg:col-span-5">
            <Kicker className="mb-5">{tx("Hyttene", "The cabins")}</Kicker>
            <Headline>{tx("Saman i ei hytte ved sjøen", "Together in a cabin by the sea")}</Headline>
            <Lead>{tx("Reiser de med familie eller vener? Hyttene har plass til inntil åtte, med stove, kjøkenkrok og eigen terrasse mot fjorden.", "Travelling with family or friends? The cabins sleep up to eight, with a living room, kitchenette and a private terrace facing the fjord.")}</Lead>
            <div className="mt-8" data-testid="home-cabin-facts">
              <Rows rows={lang === "en" ? CABIN_FACTS.en : CABIN_FACTS.nn} labelWidth="sm:grid-cols-[7rem_1fr]" />
            </div>
            <Button asChild variant="outline" size="lg" className="mt-8" data-testid="home-cabins-cta"><Link to="/rom#hytter">{tx("Sjå hyttene", "See the cabins")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
          </Reveal>
          <Reveal className="order-1 lg:order-2 lg:col-span-7">
            <ParallaxImg src="/images/shore-pier.jpg" alt={tx("Strandlinja ved hotellet med brygge og naust", "The shoreline by the hotel with the pier and boathouses")} className="aspect-[4/3] lg:aspect-[16/11]" position="50% 60%" range={4} sizes={SIZES.half} />
          </Reveal>
        </Container>
      </section>

      {/* Restaurant Baronen – ink band; the hall at dusk breaks the top edge of the band */}
      <section className="grain mt-28 bg-fjord text-white lg:mt-52" data-testid="home-dining">
        <Container className="grid items-center gap-12 py-20 lg:grid-cols-12 lg:gap-16 lg:py-24">
          <Reveal className="lg:col-span-5 lg:-mt-48 lg:self-start">
            <ParallaxImg src="/images/restaurant-dusk.jpg" alt={tx("Restaurant Baronen i skumringa – dekka bord, båten i taket og fjorden utanfor", "Restaurant Baronen at dusk – set tables, the boat in the ceiling and the fjord outside")} className="aspect-[4/5]" position="50% 50%" range={4} sizes={SIZES.half} />
          </Reveal>
          <Reveal delay={0.05} className="lg:col-span-6 lg:col-start-7 lg:py-6">
            <Kicker light className="mb-5">{tx("Restaurant Baronen", "Restaurant Baronen")}</Kicker>
            <Headline light size="xl" testid="home-dining-title">{tx("Eit bord med utsikt", "A table with a view")}</Headline>
            <Lead light>{tx("Lunsj i glasdelen eller ein middag med fleire rettar. I Baronen lagar me maten frå botnen av, med vekt på råvarer frå Rosendal og Hardanger.", "Lunch in the glass pavilion or a dinner of several courses. At Baronen we make everything from scratch, with produce from Rosendal and Hardanger at the centre.")}</Lead>
            <div className="mt-10">
              <Rows light rows={DINING_ROWS.map((d) => (lang === "en" ? d.en : d.nn))} testid="home-dining-rhythm" labelWidth="sm:grid-cols-[7.5rem_1fr]" />
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" variant="inverse" data-testid="home-dining-cta"><Link to="/mat">{tx("Sjå restaurant og opningstider", "Restaurant and opening hours")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
              <Button asChild size="lg" variant="outline-light" data-testid="home-dining-table-cta"><Link to="/mat#bord">{tx("Spør om bord", "Ask about a table")}</Link></Button>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-white/55">{tx("Middag må bestillast på førehand. Opningstidene kan variere i enkelte periodar.", "Dinner must be booked in advance. Opening hours may vary in certain periods.")}</p>
          </Reveal>
        </Container>
      </section>

      {/* Rosendal – pinned horizontal rail on desktop, staggered grid on phones */}
      <RosendalRail />

      {/* Events – index list, not a card grid */}
      <section className="pt-28 lg:pt-40" data-testid="home-events">
        <Container>
          <RuleDraw className="mb-14 lg:mb-20" />
          <Reveal>
            <SectionHead
              size="lg"
              kicker={tx("Selskap og møte", "Events and meetings")}
              title={tx("Samlast i Rosendal", "Gather in Rosendal")}
              lead={tx("Bryllaup, jubileum eller ei samling med kollegaer. Hos oss kan de samle lokale, servering og overnatting på same stad.", "A wedding, an anniversary or a gathering with colleagues. With us you can bring the venue, the food and the rooms together in one place.")}
              action={tx("Fortel oss om planane", "Tell us about your plans")}
              to="/arrangement#forespurnad"
              testid="home-events-title"
            />
          </Reveal>
          <div className="mt-12 border-t border-border lg:mt-16">
            {HOME_EVENTS.map((id, i) => {
              const fb = EVENT_FALLBACK[id];
              const [t, body, cta] = lang === "en" ? fb.en : fb.nn;
              const img = eventTypes[id]?.image || fb.img;
              return (
                <Reveal key={id} delay={i * 0.04}>
                  <Link to={`/arrangement?type=${id}#forespurnad`} className="group grid gap-6 border-b border-border py-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 sm:grid-cols-12 sm:items-center sm:gap-8 lg:py-10" data-testid={`home-event-${id}`}>
                    <span className="index-num hidden text-muted-foreground tnum sm:col-span-1 sm:block">{String(i + 1).padStart(2, "0")}</span>
                    <div className="sm:col-span-4">
                      <Img src={img} alt={t} className="img-zoom aspect-[3/2]" position={fb.position} sizes={SIZES.third} />
                    </div>
                    <div className="sm:col-span-6 sm:pl-2 lg:pl-6">
                      <h3 className="text-[1.5rem] font-medium leading-[1.15] tracking-[-0.02em] text-foreground sm:text-[1.75rem]">{t}</h3>
                      <p className="mt-3 max-w-[50ch] text-[1rem] leading-[1.6] text-muted-foreground">{body}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-[0.9375rem] font-medium text-foreground"><span className="link-grow">{cta}</span><ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.5} /></span>
                    </div>
                    <span className="hidden justify-self-end text-muted-foreground transition-[color,transform] duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground sm:col-span-1 sm:block"><ArrowUpRight className="h-5 w-5" strokeWidth={1.25} /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Fjordverten – a quiet sand band */}
      <section className="mt-28 bg-sand lg:mt-40" data-testid="home-concierge">
        <Container className="grid gap-12 py-20 lg:grid-cols-12 lg:gap-16 lg:py-28">
          <Reveal className="lg:col-span-6">
            <Kicker className="mb-5">{tx("Digital hjelp heile døgnet", "Digital help around the clock")}</Kicker>
            <Headline>{tx("Lurer du på noko før du kjem?", "Wondering about something before you arrive?")}</Headline>
            <Lead>
              {tx("Fjordverten er KI-assistenten vår. Spør om rom, mat eller praktiske detaljar ved opphaldet. Vil du snakke med resepsjonen, ring", "Fjordverten is our AI assistant. Ask about rooms, food or the practical details of your stay. If you would rather talk to reception, call")}{" "}
              <a href={PHONE.tel} className="whitespace-nowrap font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground tabular">{PHONE.display}</a>.
            </Lead>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => ask(null)} data-testid="home-concierge-open"><MessageCircle className="h-4 w-4" strokeWidth={1.5} /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
              <Button asChild size="lg" variant="outline" className="tabular" data-testid="home-call-cta"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring resepsjonen", "Call reception")}</a></Button>
            </div>
          </Reveal>
          <Reveal delay={0.05} className="lg:col-span-5 lg:col-start-8">
            <p className="label-caps text-muted-foreground">{tx("Forslag til spørsmål", "Suggested questions")}</p>
            <ul className="mt-4 divide-y divide-foreground/10 border-y border-foreground/10">
              {questions.map((q, i) => (
                <li key={q}>
                  <button type="button" onClick={() => ask(q)} data-testid={`home-concierge-chip-${i}`} className="group flex w-full items-center justify-between gap-6 py-4 text-left text-[1.0625rem] text-foreground transition-colors hover:text-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <span>{q}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          </Reveal>
        </Container>
      </section>

      {/* Closing */}
      <ClosingBand
        testid="home-closing"
        size="xl"
        height="h-[86svh] max-h-[900px] min-h-[520px]"
        title={tx("Når vil du kome?", "When would you like to come?")}
        lead={tx("Vel datoane dine og sjå ledige rom. Frukost er inkludert i alle hotellrom.", "Choose your dates and see available rooms. Breakfast is included in all hotel rooms.")}
        alt={tx("Brygga ut i Hardangerfjorden ved hotellet", "The pier into the Hardangerfjord by the hotel")}
      >
        <Button asChild size="lg" variant="inverse" data-testid="home-closing-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
        <Button asChild size="lg" variant="outline-light" className="tabular" data-testid="home-closing-phone"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring", "Call")} {PHONE.display}</a></Button>
      </ClosingBand>
    </div>
  );
}
